import { z } from 'zod';

/**
 * Zod schemas for identity/user-related entities
 * These provide runtime validation and type inference
 */

// UserRole enum (imported from core)
// We'll use z.enum with the actual values
export const userRoleSchema = z.enum([
  'ADMIN',
  'PLATFORM_ADMIN',
  'PROPERTY_MANAGER',
  'TENANT',
  'OWNER',
  'VENDOR',
  'INVESTOR',
  'SYSTEM_ADMIN',
] as const);

// UserProfile schema
export const userProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  fullName: z.string().min(1, 'Full name is required'),
  phone: z.string(),
  avatar: z.string(),
  emailVerified: z.boolean(),
  lastLoginAt: z
    .union([z.string(), z.date(), z.record(z.unknown())])
    .transform((v) =>
      typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : ''
    )
    .catch(''),
  twoFactorEnabled: z.boolean(),
  role: userRoleSchema,
  isSelfManagingOwner: z.boolean().optional(),
  tenantId: z.string().nullish(), // Link to Tenant.id; API may return null for PM/other roles
  ownerId: z.string().nullish(),
  vendorId: z.string().nullish(),
  investorId: z.string().nullish(),
  jobTitle: z.string().nullish(),
  employeeId: z.string().nullish(),
  department: z.string().nullish(),
  license: z.string().nullish(),
  settings: z.record(z.unknown()).nullish(),
  orgLevelStripeCustomerId: z.string().nullish(),
});

export type UserProfile = z.infer<typeof userProfileSchema>;
